import moment from "moment";

export type ServiceReminderStatus = "overdue" | "dueSoon" | "ok" | "none";

/**
 * Number of days within which an upcoming service is considered "due soon".
 */
export const SERVICE_REMINDER_DUE_SOON_DAYS = 7;

/**
 * Classifies a service reminder by its next due date.
 * - "none"    → no due date set
 * - "overdue" → due date is before today
 * - "dueSoon" → due within the next {@link SERVICE_REMINDER_DUE_SOON_DAYS} days (inclusive)
 * - "ok"      → due further in the future
 *
 * Comparison is done on calendar days (start of day), so a reminder due "today"
 * counts as dueSoon, not overdue.
 */
export const getServiceReminderStatus = (nextDueDate?: string | null): ServiceReminderStatus => {
    if (!nextDueDate) return "none";

    const due = moment(nextDueDate);
    if (!due.isValid()) return "none";

    const startOfToday = moment().startOf("day");
    const dueDay = due.clone().startOf("day");
    const daysUntilDue = dueDay.diff(startOfToday, "days");

    if (daysUntilDue < 0) return "overdue";
    if (daysUntilDue <= SERVICE_REMINDER_DUE_SOON_DAYS) return "dueSoon";
    return "ok";
};

/**
 * True when a reminder needs attention (overdue or due soon) — used to decide
 * whether the dashboard widget should deep-link straight to the reset dialog.
 */
export const isServiceReminderActionable = (status: ServiceReminderStatus): boolean =>
    status === "overdue" || status === "dueSoon";

type WithDueDate = { nextDueDate?: string | null };

/**
 * Sorts reminders by next due date ascending; reminders without a due date sink
 * to the bottom. Returns a new array (does not mutate the input).
 */
export const sortByNextDueDate = <T extends WithDueDate>(reminders: T[]): T[] =>
    [...reminders].sort((a, b) => {
        if (!a.nextDueDate) return 1;
        if (!b.nextDueDate) return -1;
        return moment(a.nextDueDate).valueOf() - moment(b.nextDueDate).valueOf();
    });

/**
 * Returns the most urgent reminder (earliest due date) from a list, or undefined
 * when the list is empty.
 */
export const getNextDueReminder = <T extends WithDueDate>(reminders: T[]): T | undefined =>
    sortByNextDueDate(reminders)[0];
